<script lang="ts">
import { useQuery } from '@urql/vue'
import { onClickOutside, templateRef, useMagicKeys } from '@vueuse/core'
import type { PropType } from 'vue'
import { computed, defineComponent, inject, ref, watch } from 'vue'

import { countryInjectionKey } from '@/App.vue'
import ActionChip from '@/atoms/ActionChip.vue'
import CtaButton from '@/atoms/CtaButton.vue'
import { useHostURL } from '@/functions/useHostURL'
import { useQueryVariables } from '@/functions/useQueryVariables'
import type { HomePage } from '@/graphql'
import GetLanguageSwitcher from '@/graphql/GetLanguageSwitcher.gql'
import ButtonIcon from '@/molecules/ButtonIcon.vue'
import { Theme } from '@/types'

type LanguageSwitcher = Pick<HomePage, 'languageSwitcherCountry' | 'languageSwitcherShipping' | 'languageSwitcherTitle'>

export default defineComponent({
  name: 'LanguageSwitcher',
  components: {
    ActionChip,
    ButtonIcon,
    CtaButton
  },
  props: {
    theme: { type: String as PropType<`${Theme}`>, default: undefined }
  },
  setup (props) {
    const { countries: countriesURL, getLocalizedURL } = useHostURL()
    const { escape } = useMagicKeys()
    const active = ref(false)
    const country = inject(countryInjectionKey)
    const wrapper = templateRef<HTMLElement>('wrapper')
    const query = useQuery({ query: GetLanguageSwitcher, variables: useQueryVariables() })
    const data = computed<LanguageSwitcher>(() => query.data.value?.languageSwitcher)

    const uiTheme = computed(() => {
      if (!props.theme) return
      return props.theme === Theme.DARK ? Theme.LIGHT : Theme.DARK
    })

    onClickOutside(wrapper, () => (active.value = false))

    watch(escape, pressed => pressed && (active.value = false))

    return {
      data,
      active,
      country,
      countriesURL,
      uiTheme,
      getLocalizedURL
    }
  }
})
</script>

<template>
  <div ref="wrapper" class="relative">
    <Transition>
      <div
        v-if="active && data"
        class="panel panel-light min-w-56 max-w-full p-5 my-2 absolute left-0 bottom-[100%] text-center"
      >
        <div class="my-5 relative z-2">
          <div v-if="country.locales.length > 1">
            <span class="h5">{{ data.languageSwitcherTitle }}</span>
            <nav class="mt-4">
              <ActionChip
                v-for="locale, i in country.locales"
                :key="`${country.code}-${locale}`"
                :class="{ 'ml-4': i > 0 }"
                :active="locale === $i18n.locale"
                :href="getLocalizedURL(locale)"
                :data-track-cta-name="`Change language: ${locale.toUpperCase()}`"
                target="_parent"
              >
                <span class="uppercase">{{ locale }}</span>
              </ActionChip>
            </nav>
          </div>
          <div :class="{ 'mt-5': country.locales.length > 1 }">
            <p>
              <small class="uppercase opacity-50">{{ data.languageSwitcherShipping }}</small>&nbsp;
              <span>{{ $t(country.name) }}</span>
            </p>
            <CtaButton
              class="mt-4"
              :href="countriesURL"
              :theme="uiTheme"
              :negative="!uiTheme"
              target="_parent"
              secondary
              black-outline
              :data-track-cta-name="data.languageSwitcherCountry"
            >
              {{ data.languageSwitcherCountry }}
            </CtaButton>
          </div>
        </div>
      </div>
    </Transition>
    <ButtonIcon class="toggle" icon="language" :theme="theme" @click="active = !active">
      <span class="over-h relative z-1 uppercase">{{ $i18n.locale }}</span>
    </ButtonIcon>
  </div>
</template>

<style lang="scss" scoped>
.toggle > span {
  padding-top: 0.1em;
  margin-left: 0.1em;
}

.panel {
  border-radius: rem(4px);
  box-shadow: 1px 0 20px 0 rgba(0, 0, 0, 15%);

  @include paper-background;

  &.panel-light {
    background-color: $c-white;
    color: $c-black;
  }

  .decoration-arrow {
    opacity: 0.5;
  }
}

</style>
